import React, { useState, useCallback } from 'react';
import {
  FaClipboardList,
  FaFolderOpen,
  FaUser,
  FaToolbox,
} from 'react-icons/fa';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { Shift } from '../../shifts/services/ShiftService';
import { BacklogMeta, ProjectTimelineItem } from '../services/PlantafelService';
import { Absence } from '../../absences/services/AbsenceService';
import { TabKey, AssetAssignment, EmployeeAssignment, DayInfo } from './leftPanelTypes';
import { LeftPanelBacklog } from './LeftPanelBacklog';
import { LeftPanelProjects } from './LeftPanelProjects';
import { LeftPanelEmployees } from './LeftPanelEmployees';
import { LeftPanelAssets } from './LeftPanelAssets';
import styles from './PlantafelLeftPanel.module.css';

interface PlantafelLeftPanelProps {
  tasks: CrmTask[];
  meta: BacklogMeta | null;
  selectedShift: Shift | null;
  onTaskClick: (task: CrmTask) => void;
  onUnscheduleShift?: (shiftId: number) => void;
  onRefresh?: () => Promise<void>;
  assignedAssetMap?: Map<number, AssetAssignment>;
  assignedEmployeeMap?: Map<number, EmployeeAssignment>;
  employeeHoursMap?: Map<number, number>;
  onHighlightShift?: (shiftId: number) => void;
  absences?: Absence[];
  days?: DayInfo[];
  onAbsentEmployeeClick?: (employeeId: number) => void;
  projectsTimeline?: ProjectTimelineItem[];
}

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'backlog', label: 'Jobs', icon: <FaClipboardList size={11} /> },
  { key: 'projekte', label: 'Projekte', icon: <FaFolderOpen size={11} /> },
  { key: 'mitarbeiter', label: 'MA', icon: <FaUser size={11} /> },
  { key: 'assets', label: 'Assets', icon: <FaToolbox size={11} /> },
];

export default function PlantafelLeftPanel({
  tasks,
  meta,
  selectedShift,
  onTaskClick,
  onUnscheduleShift,
  onRefresh,
  assignedAssetMap,
  assignedEmployeeMap,
  employeeHoursMap,
  onHighlightShift,
  absences = [],
  days = [],
  onAbsentEmployeeClick,
  projectsTimeline = [],
}: PlantafelLeftPanelProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<TabKey>('backlog');
  const [isDropTarget, setIsDropTarget] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    const hasShiftId = types.some((t) => t.toLowerCase() === 'shiftid');
    if (hasShiftId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    const hasShiftId = types.some((t) => t.toLowerCase() === 'shiftid');
    if (hasShiftId) {
      e.preventDefault();
      setIsDropTarget(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDropTarget(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDropTarget(false);

      const shiftId = e.dataTransfer.getData('shiftId');
      const isJobShift = e.dataTransfer.getData('isJobShift') === 'true';

      if (shiftId && isJobShift && onUnscheduleShift) {
        onUnscheduleShift(parseInt(shiftId, 10));
      }
    },
    [onUnscheduleShift]
  );

  return (
    <div
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={styles.panel}
      style={{
        background: isDropTarget ? '#fafdfb' : undefined,
        borderRight: isDropTarget ? '2px solid #bbf7d0' : undefined,
      }}
      data-testid="plantafel-backlog"
    >
      <div className={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`${styles.tabButton} ${activeTab === tab.key ? styles.tabButtonActive : ''}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'backlog' && (
          <LeftPanelBacklog
            tasks={tasks}
            meta={meta}
            onTaskClick={onTaskClick}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'projekte' && (
          <LeftPanelProjects
            isActive={activeTab === 'projekte'}
          />
        )}

        {activeTab === 'mitarbeiter' && (
          <LeftPanelEmployees
            isActive={activeTab === 'mitarbeiter'}
            assignedEmployeeMap={assignedEmployeeMap}
            employeeHoursMap={employeeHoursMap}
            onHighlightShift={onHighlightShift}
            absences={absences}
            days={days}
            onAbsentEmployeeClick={onAbsentEmployeeClick}
          />
        )}

        {activeTab === 'assets' && (
          <LeftPanelAssets
            isActive={activeTab === 'assets'}
            assignedAssetMap={assignedAssetMap}
            onHighlightShift={onHighlightShift}
          />
        )}
      </div>
    </div>
  );
}
